import { FilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import { DirectGasEmissionActivitiesList } from '@shared/api/types';
import Unit from '@shared/components/content/unit';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import { capitalizeFirstLetter, formatQuantity } from '@shared/lib/utils';
import { ColumnDef } from '@tanstack/react-table';

export function directGasTableColumns({
  filters,
}: {
  filters: FilterOptions<['timePeriods', 'sites', 'sources', 'gases']>;
}): ColumnDef<DirectGasEmissionActivitiesList>[] {
  return [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
    },
    {
      id: 'period',
      accessorKey: 'period',
      enableHiding: true,
      enableColumnFilter: true,
      meta: {
        label: 'Time period',
        variant: 'multiSelect',
        options: filters.timePeriods,
        sorting: { ascLabel: 'Oldest', descLabel: 'Newest' },
      },
    },
    {
      id: 'site',
      accessorKey: 'site',
      enableHiding: true,
      enableColumnFilter: true,
      header: ({ column }) => <HeaderCell column={column} name="Site" />,
      cell: ({ getValue }) => {
        const site = getValue<DirectGasEmissionActivitiesList['site']>();
        return <TextCell text={site.name} />;
      },
      meta: {
        label: 'Site',
        variant: 'multiSelect',
        options: filters.sites,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'source',
      accessorKey: 'source',
      enableColumnFilter: true,
      header: ({ column }) => <HeaderCell column={column} name="Source" />,
      cell: ({ getValue }) => {
        const source = getValue<DirectGasEmissionActivitiesList['source']>();
        return <TextCell text={capitalizeFirstLetter(source)} />;
      },
      meta: {
        label: 'Source',
        variant: 'multiSelect',
        options: filters.sources,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'gas',
      accessorKey: 'gas',
      header: ({ column }) => <HeaderCell column={column} name="Gas" />,
      cell: ({ getValue }) => {
        const gas = getValue<DirectGasEmissionActivitiesList['gas']>();
        return <TextCell text={gas.name} />;
      },
      enableColumnFilter: true,
      meta: {
        label: 'Gas',
        variant: 'multiSelect',
        options: filters.gases,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'emissionFactor',
      accessorKey: 'emissionFactor',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emission Factor"
          numberColumn
          unit={<Unit variant="kgCO2e/kg" />}
        />
      ),
      cell: ({ getValue }) => {
        const emissionFactor = getValue<number>();
        return <NumberCell number={emissionFactor} />;
      },
    },
    {
      id: 'quantity',
      accessorKey: 'quantity',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Quantity"
          numberColumn
          unit={<Unit variant="kg" />}
        />
      ),
      cell: ({ getValue }) => {
        const quantity = getValue<number>();
        return <NumberCell number={quantity} formatter={formatQuantity} />;
      },
    },
    {
      id: 'emissions',
      accessorKey: 'emissions',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emissions"
          numberColumn
          unit={<Unit variant="tCO2e" />}
        />
      ),
      cell: ({ getValue }) => {
        const scope3Emissions = getValue<number>();
        return <NumberCell number={scope3Emissions} />;
      },
    },
  ];
}
